import { ROTATION } from '../game/constants';
import { clamp } from '../utils/math';
import type { InputState } from './InputState';
import type { InputRouter } from './InputRouter';

/**
 * Horizontal drag distance, in CSS pixels, that maps to full board speed.
 * Smaller = twitchier.
 */
const DRAG_FULL_SPEED_PIXELS = 150;

/**
 * Keyboard + pointer-drag controls.
 *
 * These write into the shared `InputRouter` alongside the gyroscope and the
 * on-screen control bar (`MobileControls`), so there is one board and one
 * resolved angular-velocity target, never a race between devices.
 *
 *   A / D or Left / Right   hold to spin the board
 *   mouse / touch drag      drag right to spin right, left to spin left
 *   R                       respawn the ball
 */
export class DebugControls {
  private readonly keys = new Set<string>();
  private dragging = false;
  private dragOriginX = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly input: InputState,
    private readonly router: InputRouter,
  ) {
    // Stop the page scrolling or pinch-zooming underneath the game.
    this.canvas.style.touchAction = 'none';

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    this.canvas.addEventListener('contextmenu', this.onContextMenu);
  }

  /**
   * Derive `omegaTarget` from the current keyboard state.
   *
   * Called once per fixed physics step. A drag in progress wins, and a forced
   * value (set by the verification harness) wins over everything.
   */
  update(): void {
    if (this.input.forcedOmega !== null) return;
    if (this.dragging) return;

    let target = 0;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) target -= ROTATION.maxOmega;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) target += ROTATION.maxOmega;
    this.router.set('keyboard', target, target !== 0);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'KeyR') {
      this.input.resetRequested = true;
      return;
    }
    this.keys.add(event.code);
    if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
      event.preventDefault();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
  };

  private readonly onBlur = (): void => {
    this.keys.clear();
    this.dragging = false;
    this.router.clearAll();
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.input.forcedOmega !== null) return;
    this.dragging = true;
    this.dragOriginX = event.clientX;
    this.canvas.setPointerCapture(event.pointerId);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.dragging) return;
    const offset = event.clientX - this.dragOriginX;
    const gain = ROTATION.maxOmega / DRAG_FULL_SPEED_PIXELS;
    this.router.set('drag', clamp(offset * gain, -ROTATION.maxOmega, ROTATION.maxOmega), true);
  };

  private readonly onPointerUp = (): void => {
    if (!this.dragging) return;
    this.dragging = false;
    this.router.set('drag', 0, false);
  };

  private readonly onContextMenu = (event: Event): void => {
    event.preventDefault();
  };
}
